import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import {
  Button,
  Card,
  Empty,
  Skeleton,
  Tag,
  message,
  Modal,
  Tooltip,
  Switch,
  Row,
  Col,
  Statistic,
  Spin,
} from "antd";
import {
  PlusOutlined,
  GlobalOutlined,
  CopyOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  HomeOutlined,
  SafetyOutlined,
  LineChartOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  FilterOutlined,
  BulbOutlined,
  QrcodeOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import { useConnectStatus } from "../hooks/useConnectStatus";
import { getProperties } from "../lib/api/propertiesApi";
import listingModel from "../lib/firebase/listingModel";
import unitModel from "../lib/firebase/unitModel";
import CreateListingModal from "../components/marketing/CreateListingModal";
import ShareApplicationModal, {
  buildApplicationUrl,
} from "../components/marketing/ShareApplicationModal";
import { calculateTotalMonthlyCost, hasItemizedFees } from "../lib/rentUtils";
import { trackEvent } from "../helpers/mixpanel";
import { trackProduct } from "../lib/analytics/trackProduct";
import PageHeader from "../components/PageHeader";
import { StatCard, EmptyState as SharedEmptyState } from "../components/shared";
import { LAYOUT_CONSTANTS, device } from "../lib/breakpoints";
import { getDebugMode, getDebugUnitCount } from "../helpers/constants";
import { getDebugUnits, getDebugListings } from "../helpers/debugData";

/** Brand mark when a listing has no photos. */
function ListingPhotoPlaceholder({ compact = false }) {
  return (
    <PlaceholderWrap $compact={compact} aria-hidden="true">
      <PlaceholderLogo src="/logo.png" alt="" $compact={compact} />
    </PlaceholderWrap>
  );
}

/** Spinner while listing card photos load, then fade in. */
function ListingCardImage({ as: ImgComponent, src, alt, ...rest }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = React.useRef(null);

  useEffect(() => {
    setLoaded(false);
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <ImageLoadWrap>
      {!loaded && (
        <ImageLoadingOverlay aria-hidden="true">
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 28, color: "var(--color-primary, #2c5f2d)" }}
                spin
              />
            }
          />
        </ImageLoadingOverlay>
      )}
      <ImgComponent
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        $loaded={loaded}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        {...rest}
      />
    </ImageLoadWrap>
  );
}

/** Single main photo with optional prev/next arrows (no thumbnail row). */
function ListingCardPhotoCarousel({
  photos,
  address,
  onPhotoClick,
  openLabel,
  listingId,
}) {
  const [index, setIndex] = useState(0);
  const count = photos.length;
  const safeIndex = count > 0 ? Math.min(index, count - 1) : 0;
  const current = count > 0 ? photos[safeIndex] : null;
  const photosKey = `${listingId}:${photos.join("|")}`;

  useEffect(() => {
    setIndex(0);
  }, [photosKey]);

  const goPrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((i) => (i - 1 + count) % count);
  };

  const goNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((i) => (i + 1) % count);
  };

  return (
    <PhotoGallery>
      <MainPhotoFrame>
        <MainPhotoButton
          type="button"
          onClick={onPhotoClick}
          aria-label={openLabel}
        >
          {current ? (
            <ListingCardImage
              as={MainPhoto}
              src={current}
              alt={`${address || "Listing"} photo ${safeIndex + 1}`}
            />
          ) : (
            <ListingPhotoPlaceholder />
          )}
        </MainPhotoButton>
        {count > 1 && (
          <>
            <PhotoNavButton
              type="button"
              $side="left"
              onClick={goPrev}
              aria-label="Previous photo"
            >
              <LeftOutlined />
            </PhotoNavButton>
            <PhotoNavButton
              type="button"
              $side="right"
              onClick={goNext}
              aria-label="Next photo"
            >
              <RightOutlined />
            </PhotoNavButton>
          </>
        )}
      </MainPhotoFrame>
    </PhotoGallery>
  );
}

/**
 * MarketingListings - Landlord dashboard for managing vacancy listings
 *
 * Features:
 * - Create listings from units
 * - Copy shareable links
 * - Share application link + QR (open house)
 * - View analytics
 * - Manage inquiries
 * - Toggle active/inactive
 * - One listing per unit constraint
 */
const MarketingListings = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [shareQrModal, setShareQrModal] = useState(null);
  const [listingsLayout, setListingsLayout] = useState(() => {
    if (typeof window === "undefined") return "grid";
    try {
      const saved = window.localStorage.getItem("listings_layout");
      return saved === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return false;
    return window.matchMedia("(max-width: 900px)").matches;
  });

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mediaQuery = window.matchMedia("(max-width: 900px)");
    const handler = (e) => setIsNarrowViewport(e.matches);

    // Safari < 14 fallback
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, []);

  // List layout is desktop-only
  useEffect(() => {
    if (isNarrowViewport && listingsLayout === "list") {
      setListingsLayout("grid");
    }
  }, [isNarrowViewport, listingsLayout]);

  const setLayoutPreference = (next) => {
    setListingsLayout(next);
    try {
      window.localStorage.setItem("listings_layout", next);
    } catch {
      // ignore
    }
    trackEvent("listings_layout_changed", {
      landlord_id: user?.uid,
      layout: next,
    });
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [editingListing, setEditingListing] = useState(null);

  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();
  const debugUnitCount = getDebugUnitCount();

  // Track page view
  useEffect(() => {
    if (!isDebugMode) {
      trackEvent("marketing_listings_viewed", {
        landlord_id: user?.uid,
      });
    }
  }, [user?.uid, isDebugMode]);

  // Fetch all units for this landlord
  const { data: units = [], isLoading: isLoadingUnits } = useQuery({
    queryKey: ["units", user?.uid, isDebugMode, debugUnitCount],
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getDebugUnits(debugUnitCount));
      }
      return unitModel.getByOwner(user.uid);
    },
    enabled: !!user?.uid || isDebugMode,
  });

  // Fetch all listings for this landlord
  const { data: listings = [], isLoading: isLoadingListings } = useQuery({
    queryKey: ["listings", user?.uid, isDebugMode, debugUnitCount],
    queryFn: () => {
      if (isDebugMode) {
        const vacantCount = Math.floor(debugUnitCount * 0.15); // 15% vacant = listings (85% occupied)
        return Promise.resolve(getDebugListings(vacantCount));
      }
      return listingModel.getListingsByLandlord(user.uid);
    },
    enabled: !!user?.uid || isDebugMode,
  });

  // Payment / Connect status (passed through to create/edit modal)
  const { connectStatus } = useConnectStatus(user?.uid, {
    enablePolling: false,
  });
  const primaryAccountId =
    connectStatus?.rawStatus?.id || connectStatus?.id || null;
  const { data: propertiesResult } = useQuery({
    queryKey: ["properties", user?.uid],
    queryFn: async () => {
      try {
        const res = await getProperties();
        return res?.properties ?? [];
      } catch {
        return [];
      }
    },
    enabled: !!user?.uid && (isCreateModalOpen || isEditModalOpen),
  });
  const properties = propertiesResult ?? [];

  // Copy listing (Leo chat) link to clipboard
  const handleCopyLink = (listingId) => {
    const link = `${window.location.origin}/lease/${listingId}`;
    navigator.clipboard.writeText(link);
    message.success("Link copied to clipboard!");

    trackEvent("listing_link_copied", {
      listing_id: listingId,
      landlord_id: user.uid,
    });
  };

  // Copy public application form link
  const handleCopyApplicationLink = (listingId) => {
    const link = buildApplicationUrl(listingId);
    navigator.clipboard.writeText(link);
    message.success("Application link copied!");

    trackProduct("listing_application_link_copied", {
      listing_id: listingId,
      landlord_id: user.uid,
      source: "listings_card",
    });
  };

  // Toggle listing active/inactive
  const toggleActiveMutation = useMutation({
    mutationFn: ({ listingId, newStatus }) =>
      listingModel.updateListingStatus(listingId, newStatus),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["listings", user.uid]);
      message.success("Listing status updated");

      trackProduct("listing_status_toggled", {
        listing_id: variables.listingId,
        new_status: variables.newStatus,
        landlord_id: user.uid,
      });
    },
    onError: (error, variables) => {
      message.error("Failed to update status: " + error.message);

      trackEvent("listing_status_toggle_failed", {
        listing_id: variables.listingId,
        new_status: variables.newStatus,
        landlord_id: user.uid,
        error: error.message,
      });
    },
  });

  // Delete listing
  const deleteMutation = useMutation({
    mutationFn: (listingId) => listingModel.deleteListing(listingId),
    onSuccess: (_, listingId) => {
      queryClient.invalidateQueries(["listings", user.uid]);
      message.success("Listing deleted");

      trackProduct("listing_deleted", {
        listing_id: listingId,
        landlord_id: user.uid,
      });
    },
    onError: (error, listingId) => {
      message.error("Failed to delete listing: " + error.message);

      trackEvent("listing_delete_failed", {
        listing_id: listingId,
        landlord_id: user.uid,
        error: error.message,
      });
    },
  });

  const handleDelete = (listing) => {
    trackEvent("listing_delete_clicked", {
      listing_id: listing.id,
      landlord_id: user.uid,
      listing_address: listing.address,
    });

    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      width: 400,
      wrapClassName: "lo-compact-confirm",
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "3rem",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ExclamationCircleOutlined
              aria-label="Warning"
              style={{ color: "#000000" }}
            />
          </div>
          <h3
            style={{
              fontFamily: "var(--font-primary)",
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "var(--color-text)",
              margin: "0 0 0.75rem 0",
              lineHeight: "1.4",
            }}
          >
            Delete this listing?
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              margin: 0,
              lineHeight: "1.5",
              overflowWrap: "anywhere",
            }}
          >
            {listing.address
              ? `This will permanently remove the listing for ${listing.address}. This action cannot be undone.`
              : "This action cannot be undone."}
          </p>
        </div>
      ),
      okText: "Delete Listing",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => deleteMutation.mutate(listing.id),
      onCancel: () => {
        trackEvent("listing_delete_cancelled", {
          listing_id: listing.id,
          landlord_id: user.uid,
        });
      },
    });
  };

  const handlePreview = (listingId) => {
    window.open(`/lease/${listingId}`, "_blank");

    trackEvent("listing_previewed", {
      listing_id: listingId,
      landlord_id: user.uid,
    });
  };

  const handleListingPhotoClick = (listing) => {
    if (listing.status === "active") {
      handlePreview(listing.id);
      return;
    }
    trackEvent("listing_edit_clicked", {
      listing_id: listing.id,
      landlord_id: user.uid,
      listing_address: listing.address,
      source: "card_photo",
    });
    setEditingListing(listing);
    setIsEditModalOpen(true);
  };

  const handleStatusToggle = (listing, checked) => {
    toggleActiveMutation.mutate({
      listingId: listing.id,
      newStatus: checked ? "active" : "inactive",
    });
  };

  const isLoading = isLoadingUnits || isLoadingListings;

  // Get units that don't have listings yet
  const unitsWithListings = new Set(listings.map((l) => l.unitId));
  const availableUnits = units.filter((u) => !unitsWithListings.has(u.id));

  // Calculate stats
  const totalViews = listings.reduce(
    (sum, listing) => sum + (listing.viewCount || 0),
    0
  );
  const totalInquiries = listings.reduce(
    (sum, listing) => sum + (listing.inquiryCount || 0),
    0
  );
  const activeListings = listings.filter((l) => l.status === "active").length;

  // Loading State
  if (isLoading) {
    return (
      <Container className="listings-container">
        <PageHeader
          title="Listings"
          subtitle="Share your vacant properties and receive qualified leads 24/7"
          icon={GlobalOutlined}
        />
        <StatsSection>
          <Row gutter={[24, 24]}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} sm={12} lg={8} key={i}>
                <Card>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
        </StatsSection>
        <Card style={{ marginTop: "2rem" }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </Container>
    );
  }

  return (
    <Container className="listings-container">
      <PageHeader
        title="Listings"
        subtitle="Share your vacant properties and receive qualified leads 24/7"
        icon={GlobalOutlined}
      />

      {/* Summary Statistics */}
      {listings.length > 0 && (
        <StatsSection>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Active Listings"
                value={activeListings}
                prefix={
                  <HomeOutlined style={{ color: "var(--color-primary)" }} />
                }
                valueStyle={{ color: "var(--color-primary)" }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Total Views"
                value={totalViews}
                prefix={
                  <EyeOutlined style={{ color: "var(--color-accent)" }} />
                }
                valueStyle={{ color: "var(--color-accent)" }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                className="stats-card"
                title="Total Inquiries"
                value={totalInquiries}
                prefix={
                  <GlobalOutlined style={{ color: "var(--color-primary)" }} />
                }
                valueStyle={{ color: "var(--color-primary)" }}
              />
            </Col>
          </Row>
        </StatsSection>
      )}

      {/* Empty State for No Listings (but has units) */}
      {listings.length === 0 &&
        units.length > 0 &&
        availableUnits.length > 0 && (
          <EmptyStateContainer>
            <EmptyStateHeader>
              <EmptyStateIcon>
                <HomeOutlined />
              </EmptyStateIcon>
              <EmptyStateTitle>
                Get a Shareable Link for Your Property
              </EmptyStateTitle>
              <EmptyStateSubtitle>
                Create a listing, get a link, and post it on Facebook,
                Craigslist, or Zillow. Leo, your AI assistant, will help screen
                tenants and answer questions 24/7.
              </EmptyStateSubtitle>
              <EmptyStateCTATop>
                <Button
                  type="primary"
                  size="large"
                  icon={
                    <PlusOutlined
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  }
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Create Your First Listing
                </Button>
                <CTASubtext>
                  Takes 2 minutes • Get your link • Post anywhere
                </CTASubtext>
              </EmptyStateCTATop>
            </EmptyStateHeader>

            <BenefitsSection>
              <BenefitsSectionTitle>
                How It Works: Your Link Does the Screening
              </BenefitsSectionTitle>
            </BenefitsSection>

            <BenefitsGrid>
              <BenefitCard>
                <BenefitIcon>
                  <FilterOutlined />
                </BenefitIcon>
                <BenefitTitle>Pre-Qualify Prospects</BenefitTitle>
                <BenefitDescription>
                  Leo screens prospects based on your requirements. Set credit
                  score and income thresholds upfront. Only qualified leads
                  reach you.
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitIcon>
                  <SafetyOutlined />
                </BenefitIcon>
                <BenefitTitle>Built-In Screening</BenefitTitle>
                <BenefitDescription>
                  Leo asks prospects about credit, income, and pets before they
                  can contact you. Save hours of back-and-forth with automated
                  screening.
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitIcon>
                  <LineChartOutlined />
                </BenefitIcon>
                <BenefitTitle>Track Everything</BenefitTitle>
                <BenefitDescription>
                  See how many people viewed your listing and who inquired. Know
                  what's working.
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitIcon>
                  <LinkOutlined />
                </BenefitIcon>
                <BenefitTitle>One Link, Every Platform</BenefitTitle>
                <BenefitDescription>
                  Post your link on Facebook Marketplace, Craigslist, Zillow -
                  wherever you advertise. One link for all platforms.
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitIcon>
                  <ThunderboltOutlined />
                </BenefitIcon>
                <BenefitTitle>Instant Notifications</BenefitTitle>
                <BenefitDescription>
                  Get alerted the moment someone inquires. Respond fast and fill
                  vacancies faster.
                </BenefitDescription>
              </BenefitCard>

              <BenefitCard>
                <BenefitIcon>
                  <TrophyOutlined />
                </BenefitIcon>
                <BenefitTitle>Professional Presentation</BenefitTitle>
                <BenefitDescription>
                  Beautiful, mobile-friendly listing page. Stand out from
                  Craigslist and Facebook spam.
                </BenefitDescription>
              </BenefitCard>
            </BenefitsGrid>
          </EmptyStateContainer>
        )}

      {/* Empty State for No Doors */}
      {units.length === 0 && (
        <SharedEmptyState
          icon={<HomeOutlined />}
          title="No Properties Yet"
          subtitle="Add your first property on the Doors page to create a listing."
        />
      )}

      {/* Empty State for No Available Doors */}
      {units.length > 0 &&
        availableUnits.length === 0 &&
        listings.length === 0 && (
          <SharedEmptyState
            icon={<CheckCircleOutlined />}
            title="All Doors Have Listings"
            subtitle="All your properties already have active listings. Great job!"
          />
        )}

      {/* Listings Section */}
      {listings.length > 0 && (
        <>
          <ActionsBar>
            <ViewToggleWrap>
              <Tooltip title="Card view">
                <ViewToggleButton
                  type={listingsLayout === "grid" ? "primary" : "default"}
                  icon={<AppstoreOutlined />}
                  onClick={() => setLayoutPreference("grid")}
                  aria-label="Card view"
                />
              </Tooltip>
              <Tooltip title="List view (desktop)">
                <ViewToggleButton
                  type={listingsLayout === "list" ? "primary" : "default"}
                  icon={<UnorderedListOutlined />}
                  onClick={() => setLayoutPreference("list")}
                  aria-label="List view"
                />
              </Tooltip>
            </ViewToggleWrap>
            {(() => {
              const createDisabled = availableUnits.length === 0;
              const disabledReason = createDisabled
                ? "All your doors already have listings. You can only create one listing per door."
                : null;

              return (
                <CreateActions>
                  <Tooltip title={disabledReason} placement="top">
                    <span>
                      <Button
                        type="primary"
                        size="large"
                        icon={
                          <PlusOutlined
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        onClick={() => {
                          trackEvent("create_listing_button_clicked", {
                            landlord_id: user.uid,
                            available_units_count: availableUnits.length,
                            existing_listings_count: listings.length,
                          });
                          setIsCreateModalOpen(true);
                        }}
                        disabled={createDisabled}
                      >
                        Create New Listing
                      </Button>
                    </span>
                  </Tooltip>
                  {createDisabled && disabledReason && (
                    <MobileDisabledReason>
                      {disabledReason}
                    </MobileDisabledReason>
                  )}
                </CreateActions>
              );
            })()}
          </ActionsBar>

          {listingsLayout === "list" && !isNarrowViewport ? (
            <ListingsList>
              {listings.map((listing) => {
                const unit = units.find((u) => u.id === listing.unitId);
                const baseRent =
                  listing.rentAmount ??
                  unit?.currentRent ??
                  unit?.baseRent ??
                  0;
                const fees =
                  listing.additional_mandatory_fees ??
                  unit?.additional_mandatory_fees ??
                  [];
                const totalMonthlyCost = calculateTotalMonthlyCost({
                  baseRent,
                  additionalMandatoryFees: fees,
                });
                const listingPhotos = (
                  Array.isArray(listing.photos) && listing.photos.length > 0
                    ? listing.photos
                    : Array.isArray(unit?.photos)
                      ? unit.photos
                      : []
                ).filter((url) => typeof url === "string" && url.trim());
                const thumb = listingPhotos[0] || null;
                return (
                  <ListRow key={listing.id}>
                    <ListThumbButton
                      type="button"
                      onClick={() => handleListingPhotoClick(listing)}
                      aria-label={
                        listing.status === "active"
                          ? "Open live listing"
                          : "Edit listing"
                      }
                    >
                      {thumb ? (
                        <ListingCardImage as={ListThumb} src={thumb} alt="" />
                      ) : (
                        <ListThumbPlaceholder>
                          <ListingPhotoPlaceholder compact />
                        </ListThumbPlaceholder>
                      )}
                    </ListThumbButton>

                    <ListMain>
                      <ListAddress>
                        {listing.address || "Address not specified"}
                        {listing.city && listing.state
                          ? `, ${listing.city}, ${listing.state}`
                          : ""}
                      </ListAddress>
                      <ListMeta>
                        ${totalMonthlyCost.toLocaleString()}/mo •{" "}
                        {listing.bedrooms} bed, {listing.bathrooms} bath
                      </ListMeta>
                    </ListMain>

                    <ListStats>
                      <span>
                        <strong>{listing.viewCount || 0}</strong> views
                      </span>
                      <span>
                        <strong>{listing.inquiryCount || 0}</strong> inquiries
                      </span>
                      {listing.status === "active" ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                          Live
                        </Tag>
                      ) : (
                        <PausedTag icon={<CloseCircleOutlined />}>
                          Paused
                        </PausedTag>
                      )}
                    </ListStats>

                    <ListActions>
                      <Tooltip title="Copy listing link">
                        <IconButton
                          icon={<CopyOutlined />}
                          onClick={() => handleCopyLink(listing.id)}
                        />
                      </Tooltip>
                      <Tooltip title="Copy application link">
                        <IconButton
                          icon={<FileTextOutlined />}
                          onClick={() => handleCopyApplicationLink(listing.id)}
                        />
                      </Tooltip>
                      <Tooltip title="Edit listing">
                        <IconButton
                          icon={<EditOutlined />}
                          onClick={() => {
                            trackEvent("listing_edit_clicked", {
                              listing_id: listing.id,
                              landlord_id: user.uid,
                              listing_address: listing.address,
                              source: "list_row",
                            });
                            setEditingListing(listing);
                            setIsEditModalOpen(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Preview listing">
                        <IconButton
                          icon={<EyeOutlined />}
                          onClick={() => handlePreview(listing.id)}
                        />
                      </Tooltip>
                      <Tooltip title="Application QR">
                        <IconButton
                          icon={<QrcodeOutlined />}
                          onClick={() =>
                            setShareQrModal({ listing, mode: "application" })
                          }
                        />
                      </Tooltip>
                      <Tooltip title="Delete listing">
                        <DeleteIconButton
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(listing)}
                          aria-label="Delete listing"
                        />
                      </Tooltip>
                    </ListActions>

                    <ListStatus>
                      <Switch
                        checked={listing.status === "active"}
                        onChange={(checked) =>
                          handleStatusToggle(listing, checked)
                        }
                        loading={toggleActiveMutation.isLoading}
                      />
                      <StatusLabel $active={listing.status === "active"}>
                        {listing.status === "active" ? "Active" : "Inactive"}
                      </StatusLabel>
                    </ListStatus>
                  </ListRow>
                );
              })}
            </ListingsList>
          ) : (
          <ListingsGrid>
            {listings.map((listing) => {
              const unit = units.find((u) => u.id === listing.unitId);
              const isSyndicated =
                unit &&
                (unit.marketing_enabled === true ||
                  (unit.marketing_enabled === undefined &&
                    unit.marketing_active === true));
              const baseRent =
                listing.rentAmount ?? unit?.currentRent ?? unit?.baseRent ?? 0;
              const fees =
                listing.additional_mandatory_fees ??
                unit?.additional_mandatory_fees ??
                [];
              const totalMonthlyCost = calculateTotalMonthlyCost({
                baseRent,
                additionalMandatoryFees: fees,
              });
              const showBaseRentBelow =
                fees.length > 0 && totalMonthlyCost !== baseRent;
              const hasTransparency =
                hasItemizedFees(listing) || hasItemizedFees(unit);
              const listingPhotos = (
                Array.isArray(listing.photos) && listing.photos.length > 0
                  ? listing.photos
                  : Array.isArray(unit?.photos)
                    ? unit.photos
                    : []
              ).filter((url) => typeof url === "string" && url.trim());
              const onPhotoClick = (e) => {
                e.stopPropagation();
                handleListingPhotoClick(listing);
              };
              return (
                <StyledCard key={listing.id}>
                  <ListingCardFrame>
                    <CardToolbar>
                      <ToolbarActions>
                        <Tooltip
                          title="Delete listing"
                          trigger={["hover"]}
                          mouseEnterDelay={0.5}
                        >
                          <DeleteIconButton
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(listing);
                            }}
                            aria-label="Delete listing"
                          />
                        </Tooltip>
                        <Tooltip
                          title="Edit listing"
                          trigger={["hover"]}
                          mouseEnterDelay={0.5}
                        >
                          <DeleteIconButton
                            type="text"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              trackEvent("listing_edit_clicked", {
                                listing_id: listing.id,
                                landlord_id: user.uid,
                                listing_address: listing.address,
                              });
                              setEditingListing(listing);
                              setIsEditModalOpen(true);
                            }}
                            aria-label="Edit listing"
                          />
                        </Tooltip>
                      </ToolbarActions>
                      <StatusSwitch>
                        <Switch
                          checked={listing.status === "active"}
                          onChange={(checked) => {
                            toggleActiveMutation.mutate({
                              listingId: listing.id,
                              newStatus: checked ? "active" : "inactive",
                            });
                          }}
                          loading={toggleActiveMutation.isLoading}
                        />
                        <StatusLabel $active={listing.status === "active"}>
                          {listing.status === "active" ? "Active" : "Inactive"}
                        </StatusLabel>
                      </StatusSwitch>
                    </CardToolbar>

                    <ListingCardHeader>
                      <PropertyAddress>
                        {listing.address || "Address not specified"}
                        {listing.city && listing.state && (
                          <AddressLocation>
                            , {listing.city}, {listing.state}{" "}
                            {listing.zipCode || ""}
                          </AddressLocation>
                        )}
                        {!listing.address && listing.city && listing.state && (
                          <AddressLocation>
                            {listing.city}, {listing.state}{" "}
                            {listing.zipCode || ""}
                          </AddressLocation>
                        )}
                      </PropertyAddress>
                      {listing.status === "active" ? (
                        <StageLiveTag color="success" icon={<CheckCircleOutlined />}>
                          Live
                        </StageLiveTag>
                      ) : (
                        <PausedTag icon={<CloseCircleOutlined />}>
                          Paused
                        </PausedTag>
                      )}
                    </ListingCardHeader>

                    <ListingCardPhotoCarousel
                      listingId={listing.id}
                      photos={listingPhotos}
                      address={listing.address}
                      onPhotoClick={onPhotoClick}
                      openLabel={
                        listing.status === "active"
                          ? "Open live listing"
                          : "Edit listing"
                      }
                    />

                    <ListingCardTextBox>
                      <PropertyInfo>
                      <PropertyDetails>
                        <span>
                          ${totalMonthlyCost.toLocaleString()}/mo
                          {showBaseRentBelow && (
                            <span
                              style={{
                                display: "block",
                                fontSize: "inherit",
                                color: "#666",
                                fontWeight: 400,
                              }}
                            >
                              Base rent: ${baseRent.toLocaleString()}/mo
                            </span>
                          )}
                        </span>
                        <span>
                          {" "}
                          • {listing.bedrooms} bed, {listing.bathrooms} bath
                        </span>
                        {hasTransparency && (
                          <Tooltip title="Fully itemized fee structure (FTC compliant)">
                            <Tag
                              color="green"
                              style={{ marginLeft: 8 }}
                              icon={<SafetyOutlined />}
                            >
                              Transparency
                            </Tag>
                          </Tooltip>
                        )}
                        {isSyndicated && (
                          <Tooltip title="Included in Zillow / HotPads feed">
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              Zillow / HotPads
                            </Tag>
                          </Tooltip>
                        )}
                      </PropertyDetails>
                      </PropertyInfo>

                      <StatsRow>
                      <Stat>
                        <StatValue>{listing.viewCount || 0}</StatValue>
                        <StatLabel>Views</StatLabel>
                      </Stat>
                      <Stat>
                        <StatValue>{listing.inquiryCount || 0}</StatValue>
                        <StatLabel>Inquiries</StatLabel>
                      </Stat>
                      <Stat>
                        <StatValue>
                          {listing.status === "active" ? (
                            <Tag color="success" icon={<CheckCircleOutlined />}>
                              Live
                            </Tag>
                          ) : (
                            <PausedTag icon={<CloseCircleOutlined />}>
                              Paused
                            </PausedTag>
                          )}
                        </StatValue>
                        <StatLabel>Status</StatLabel>
                      </Stat>
                    </StatsRow>

                    <ShareableLink>
                      <LinkLabelRow>
                        <LinkLabel>Listing link</LinkLabel>
                        <LinkLabelActions>
                          <Tooltip
                            title="Preview listing page"
                            trigger={["hover"]}
                            mouseEnterDelay={0.5}
                          >
                            <TitleIconButton
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(listing.id);
                              }}
                              aria-label="Preview listing page"
                            />
                          </Tooltip>
                        </LinkLabelActions>
                      </LinkLabelRow>
                      <LinkRow>
                        {(() => {
                          const shareableLink = `${window.location.origin.replace(
                            "http://localhost:3000",
                            "https://landlordops.com"
                          )}/lease/${listing.id}`;
                          return (
                            <LinkDisplayWrapper>
                              {isNarrowViewport ? (
                                <LinkDisplay title={shareableLink}>
                                  {shareableLink}
                                </LinkDisplay>
                              ) : (
                                <Tooltip
                                  title={shareableLink}
                                  mouseEnterDelay={0.4}
                                  overlayInnerStyle={{ maxWidth: "90vw" }}
                                  overlayClassName="desktop-link-tooltip"
                                >
                                  <LinkDisplay>{shareableLink}</LinkDisplay>
                                </Tooltip>
                              )}
                            </LinkDisplayWrapper>
                          );
                        })()}
                        <ActionButtons>
                          <Tooltip
                            title="Copy listing link"
                            trigger={["hover"]}
                            mouseEnterDelay={0.5}
                          >
                            <IconButton
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyLink(listing.id);
                              }}
                            />
                          </Tooltip>

                          <Tooltip
                            title="Show listing QR code"
                            trigger={["hover"]}
                            mouseEnterDelay={0.5}
                          >
                            <IconButton
                              icon={<QrcodeOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                trackEvent("listing_share_qr_opened", {
                                  listing_id: listing.id,
                                  landlord_id: user.uid,
                                });
                                setShareQrModal({
                                  listing,
                                  mode: "listing",
                                });
                              }}
                            />
                          </Tooltip>
                        </ActionButtons>
                      </LinkRow>
                    </ShareableLink>

                    <ShareableLink>
                      <LinkLabelRow>
                        <LinkLabelWithHint>
                          <LinkLabel>Application link</LinkLabel>
                          {!isNarrowViewport && (
                            <Tooltip
                              title="Prospects submit the form and you get their info by email (and in Onboarding). You handle background checks yourself outside LandlordOps."
                              trigger={["hover"]}
                              mouseEnterDelay={0.3}
                              overlayInnerStyle={{ maxWidth: 280 }}
                            >
                              <InfoHint
                                type="button"
                                aria-label="About application links"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <InfoCircleOutlined />
                              </InfoHint>
                            </Tooltip>
                          )}
                        </LinkLabelWithHint>
                        <LinkLabelActions>
                          <Tooltip
                            title="Preview application form"
                            trigger={["hover"]}
                            mouseEnterDelay={0.5}
                          >
                            <TitleIconButton
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = buildApplicationUrl(listing.id);
                                if (url) window.open(url, "_blank");
                                trackEvent("listing_application_previewed", {
                                  listing_id: listing.id,
                                  landlord_id: user.uid,
                                });
                              }}
                              aria-label="Preview application form"
                            />
                          </Tooltip>
                        </LinkLabelActions>
                      </LinkLabelRow>
                      <LinkRow>
                        {(() => {
                          const applyLink = buildApplicationUrl(listing.id);
                          return (
                            <LinkDisplayWrapper>
                              {isNarrowViewport ? (
                                <LinkDisplay title={applyLink}>
                                  {applyLink}
                                </LinkDisplay>
                              ) : (
                                <Tooltip
                                  title={applyLink}
                                  mouseEnterDelay={0.4}
                                  overlayInnerStyle={{ maxWidth: "90vw" }}
                                  overlayClassName="desktop-link-tooltip"
                                >
                                  <LinkDisplay>{applyLink}</LinkDisplay>
                                </Tooltip>
                              )}
                            </LinkDisplayWrapper>
                          );
                        })()}
                        <ActionButtons>
                          <Tooltip
                            title="Copy application link"
                            trigger={["hover"]}
                            mouseEnterDelay={0.5}
                          >
                            <IconButton
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyApplicationLink(listing.id);
                              }}
                            />
                          </Tooltip>
                          <Tooltip
                            title="Show QR code"
                            trigger={["hover"]}
                            mouseEnterDelay={0.5}
                          >
                            <IconButton
                              icon={<QrcodeOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                trackEvent("listing_application_qr_opened", {
                                  listing_id: listing.id,
                                  landlord_id: user.uid,
                                });
                                setShareQrModal({
                                  listing,
                                  mode: "application",
                                });
                              }}
                            />
                          </Tooltip>
                        </ActionButtons>
                      </LinkRow>
                    </ShareableLink>
                    </ListingCardTextBox>
                  </ListingCardFrame>
                </StyledCard>
              );
            })}
          </ListingsGrid>
          )}
        </>
      )}

      {/* Info Banner */}
      {listings.length > 0 && (
        <InfoBanner>
          <InfoContent>
            <InfoText>
              <strong>Pro Tip:</strong> Share the listing link for Leo chat and
              tours. For an open house, copy the application link or download
              the QR so walk-ins can apply on their phone.
            </InfoText>
          </InfoContent>
        </InfoBanner>
      )}

      <ShareApplicationModal
        open={!!shareQrModal?.listing}
        mode={shareQrModal?.mode || "application"}
        listingId={shareQrModal?.listing?.id}
        listingLabel={
          shareQrModal?.listing?.address || shareQrModal?.listing?.title
        }
        onClose={() => setShareQrModal(null)}
        onCopied={() => {
          trackEvent(
            shareQrModal?.mode === "listing"
              ? "listing_link_copied"
              : "listing_application_link_copied",
            {
              listing_id: shareQrModal?.listing?.id,
              landlord_id: user.uid,
              source: "qr_modal",
            }
          );
        }}
        onDownloaded={() => {
          trackEvent(
            shareQrModal?.mode === "listing"
              ? "listing_qr_downloaded"
              : "listing_application_qr_downloaded",
            {
              listing_id: shareQrModal?.listing?.id,
              landlord_id: user.uid,
            }
          );
        }}
      />

      {/* Create Listing Modal */}
      <CreateListingModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingListing(null);
        }}
        availableUnits={availableUnits}
        editingListing={null}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          setEditingListing(null);
          queryClient.invalidateQueries(["listings", user.uid]);
          queryClient.invalidateQueries(["units", user.uid]);
        }}
        primaryAccountId={primaryAccountId}
        properties={properties}
      />

      <CreateListingModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingListing(null);
        }}
        availableUnits={availableUnits}
        allUnits={units}
        editingListing={editingListing}
        onSuccess={() => {
          setIsEditModalOpen(false);
          setEditingListing(null);
          queryClient.invalidateQueries(["listings", user.uid]);
          queryClient.invalidateQueries(["units", user.uid]);
        }}
        primaryAccountId={primaryAccountId}
        properties={properties}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }

  &.listings-container {
    background: var(--color-bg-primary) !important;
  }
`;

const StatsSection = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;

  .ant-card {
    --stat-title-size: 0.875rem;
    --stat-value-size: 1.5rem;
    --stat-suffix-size: 1.5rem;
    --stat-icon-size: 1.5rem;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: var(--stat-title-size) !important;
    line-height: 1.25;
    font-weight: 600;
    color: var(--color-text-light);
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-suffix,
  .ant-statistic-content-prefix {
    font-size: var(--stat-suffix-size) !important;
    font-weight: 700;
    line-height: 1.2;
  }

  .ant-statistic .ant-statistic-content-prefix svg,
  .ant-statistic .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
    width: var(--stat-icon-size) !important;
    height: var(--stat-icon-size) !important;
  }

  @media (max-width: 768px) {
    .ant-card {
      --stat-title-size: 0.8rem;
      --stat-value-size: 1.25rem;
      --stat-suffix-size: 1.25rem;
      --stat-icon-size: 1.25rem;
    }
  }
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  gap: 1rem;
  flex-wrap: wrap;

  .ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-btn .anticon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-btn-primary:not(.ant-btn-disabled) .anticon {
    color: white !important;
  }

  .ant-btn-primary.ant-btn-disabled,
  .ant-btn-primary[disabled],
  .ant-btn-primary[disabled]:hover,
  .ant-btn-primary[disabled]:focus,
  .ant-btn-primary[disabled]:active {
    background: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.65) !important;
    cursor: not-allowed !important;
    opacity: 1 !important;
    box-shadow: none !important;

    .anticon {
      color: rgba(0, 0, 0, 0.65) !important;
      opacity: 1 !important;
    }
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

const ViewToggleWrap = styled.div`
  display: none;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem;
  background: #f1f5f9;
  border-radius: 10px;
  border: 1px solid #e2e8f0;

  @media (min-width: 901px) {
    display: inline-flex;
  }
`;

const ViewToggleButton = styled(Button)`
  min-width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
`;

const CreateActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
  gap: 0.25rem;

  @media (max-width: 640px) {
    width: 100%;
    align-items: stretch;
    margin-left: 0;
  }
`;

const ListingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(160px, 1.4fr) auto auto auto;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--color-bg-secondary);
  border: 2px solid #1f2937;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);

  @media (max-width: 1100px) {
    grid-template-columns: 72px minmax(140px, 1fr) auto;
    grid-template-areas:
      "thumb main status"
      "thumb stats actions";
  }
`;

const ListThumbButton = styled.button`
  width: 88px;
  height: 72px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 1100px) {
    grid-area: thumb;
    width: 72px;
    height: 64px;
  }
`;

const ListThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition: opacity 0.25s ease;
`;

const ImageLoadWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #eef2f7;
`;

const ImageLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f7;
  pointer-events: none;
`;

const ListThumbPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* Override tall placeholder height inside list thumb */
  & > div {
    height: 100% !important;
  }

  svg {
    max-width: 48px !important;
    width: 55% !important;
  }
`;

const ListMain = styled.div`
  min-width: 0;

  @media (max-width: 1100px) {
    grid-area: main;
  }
`;

const ListAddress = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListMeta = styled.div`
  margin-top: 0.2rem;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
`;

const ListStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  white-space: nowrap;

  strong {
    color: var(--color-text-primary);
  }

  @media (max-width: 1100px) {
    grid-area: stats;
  }
`;

const ListActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;

  @media (max-width: 1100px) {
    grid-area: actions;
    justify-content: flex-end;
  }
`;

const ListStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  .ant-switch-checked {
    background: var(--color-primary);
  }

  @media (max-width: 1100px) {
    grid-area: status;
    justify-self: end;
  }
`;

const MobileDisabledReason = styled.div`
  display: none;
  margin-top: 0.5rem;
  max-width: 520px;
  font-size: 0.875rem;
  line-height: 1.35;
  color: var(--color-text-secondary);
  text-align: center;

  @media ${device.mobile} {
    display: block;
    width: 100%;
    max-width: 100%;
  }
`;

const ListingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.75rem;
  margin-bottom: 2rem;
  justify-items: center;

  @media (min-width: 1100px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 2rem;
  }

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 380px;
  border-radius: 16px;
  border: 2px solid #2c5f2d !important;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.1);
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  background: linear-gradient(165deg, #f4f7f4 0%, #e8efe9 100%);
  overflow: hidden;
  background-clip: padding-box;

  &:hover {
    border-color: #234a24 !important;
    box-shadow: 0 8px 22px rgba(44, 95, 45, 0.14);
    transform: translateY(-2px);
  }

  &.ant-card {
    border-radius: 16px;
  }

  .ant-card-body {
    padding: 0;
  }

  @media ${device.mobile} {
    max-width: 100%;
  }
`;

const ListingCardFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  min-width: 0;

  @media ${device.mobile} {
    gap: 10px;
    padding: 12px;
  }
`;

const ListingCardTextBox = styled.div`
  padding: 2px 0 0;
  border-radius: 0;
  background: transparent;
  border: none;
  min-width: 0;
`;

const PhotoGallery = styled.div`
  padding: 0;
  background: transparent;
`;

const MainPhotoFrame = styled.div`
  position: relative;
  border-radius: 0;
  overflow: hidden;
  aspect-ratio: 16 / 10;
  width: 100%;
  border: none;
  box-shadow: none;
  background: #f8fafc;
`;

const MainPhotoButton = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;
  background: #f8fafc;
  cursor: pointer;
  line-height: 0;
`;

const MainPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 0;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition: opacity 0.25s ease;
`;

const PhotoNavButton = styled.button`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === "left" ? "left: 8px;" : "right: 8px;")}
  transform: translateY(-50%);
  z-index: 2;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.55);
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.75);
    color: #ffffff !important;
  }

  .anticon,
  .anticon svg {
    font-size: 12px;
    color: #ffffff !important;
    fill: #ffffff !important;
  }
`;

const PhotoCounter = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 2;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.6);
  color: #ffffff !important;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  pointer-events: none;
`;

const PlaceholderWrap = styled.div`
  width: 100%;
  height: ${(p) => (p.$compact ? "88px" : "100%")};
  aspect-ratio: ${(p) => (p.$compact ? "auto" : "16 / 10")};
  min-height: ${(p) => (p.$compact ? "88px" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  color: #94a3b8;
  border-radius: 0;

  @media (max-width: 640px) {
    height: ${(p) => (p.$compact ? "76px" : "100%")};
    min-height: ${(p) => (p.$compact ? "76px" : "0")};
  }
`;

const PlaceholderLogo = styled.img`
  width: ${(p) => (p.$compact ? "42%" : "32%")};
  max-width: ${(p) => (p.$compact ? "48px" : "72px")};
  height: auto;
  object-fit: contain;
  opacity: 0.72;
  filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.08));
`;

const CardHeader = styled.div`
  padding: 1rem 1.5rem 1.25rem;
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  background: linear-gradient(
    135deg,
    rgba(134, 228, 157, 0.05) 0%,
    rgba(248, 251, 239, 0.05) 100%
  );

  @media (max-width: 640px) {
    padding: 0.875rem 1rem 1rem;
    gap: 0.625rem;
  }
`;

const CardToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  background: transparent;
  border: none;
`;

const ToolbarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const ListingCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
`;

const StageLiveTag = styled(Tag)`
  margin: 0 !important;
  flex-shrink: 0;
  align-self: flex-start;
`;

const PropertyInfo = styled.div`
  width: 100%;
  min-width: 0;
  text-align: left;
  padding-bottom: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e4ebe5;
  font-size: 0.875rem;
  line-height: 1.4;
  font-family: var(--font-secondary);

  @media (max-width: 640px) {
    font-size: 0.8125rem;
  }
`;

const PropertyAddress = styled.div`
  font-size: 1.05rem !important;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  font-family: var(--font-primary, inherit) !important;
  line-height: 1.35;
  word-break: break-word;
  text-align: left;
  min-width: 0;
  flex: 1 1 auto;
`;

const AddressLocation = styled.span`
  font-size: inherit !important;
  font-weight: inherit;
  font-family: inherit !important;
  color: inherit;
  line-height: inherit;
`;

const PropertyDetails = styled.div`
  font-size: 0.8125rem !important;
  color: var(--color-text-secondary);
  font-weight: 500;
  margin-top: 0;
  text-align: left;
  line-height: 1.4;
  font-family: inherit !important;

  span,
  strong {
    font-size: inherit !important;
    font-family: inherit !important;
    line-height: inherit;
  }
`;

const StatusSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin-left: auto;

  .ant-switch-checked {
    background: var(--color-primary);
  }
`;

const StatusLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) =>
    props.$active
      ? "var(--color-success)"
      : "#6B7280"}; /* More visible gray for inactive */
  white-space: nowrap;
  opacity: 1; /* Always fully visible */

  /* Ensure good contrast */
  ${(props) =>
    !props.$active &&
    `
    color: #6B7280;
    font-weight: 500;
  `}
`;

const CardBody = styled.div`
  padding: 1.5rem;
  background: var(--color-bg-secondary);

  @media (max-width: 640px) {
    padding: 1.25rem 1rem;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f0;

  @media (max-width: 640px) {
    gap: 0.4rem;
    margin-bottom: 10px;
    padding-bottom: 10px;
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 0.2rem;

  @media (max-width: 640px) {
    font-size: 1.125rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-secondary);

  @media (max-width: 640px) {
    font-size: 0.6875rem;
  }
`;

const PausedTag = styled(Tag)`
  background: #fef3c7 !important;
  border-color: #fcd34d !important;
  color: #92400e !important;
  font-weight: 600 !important;

  .anticon {
    color: #92400e !important;
  }
`;

const ShareableLink = styled.div`
  background: #f4f8f4;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  border: 1px solid #d7e3d8;
  transition: border-color 0.15s ease, background 0.15s ease;
  text-align: left;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #eaf3ea;
    border-color: #b7cbb8;
  }

  @media (max-width: 640px) {
    padding: 10px;
    margin-bottom: 8px;
  }
`;

const LinkLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--color-primary-dark);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: left;
  margin: 0;
`;

const LinkLabelWithHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
`;

const LinkLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
  min-height: 24px;
`;

const LinkLabelActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
  margin-left: auto;
`;

const InfoHint = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-primary-dark);
  font-size: 13px;
  cursor: help;
  opacity: 0.85;
  line-height: 1;
  min-width: 24px;
  height: 24px;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }
`;

const TitleIconButton = styled(Button)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-width: 24px !important;
  width: 24px;
  height: 24px;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #111827 !important;

  .anticon {
    font-size: 14px;
    color: #111827 !important;
  }

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    color: var(--color-primary-dark) !important;

    .anticon {
      color: var(--color-primary-dark) !important;
    }
  }
`;

const DeleteIconButton = styled(Button)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-width: 28px !important;
  width: 28px;
  height: 28px;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #111827 !important;

  .anticon {
    font-size: 16px;
    color: #111827 !important;
  }

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    color: #111827 !important;

    .anticon {
      color: #111827 !important;
    }
  }
`;

const LinkRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
  position: relative;
  max-width: 100%;
  box-sizing: border-box;
`;

const LinkDisplayWrapper = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const LinkDisplay = styled.div`
  font-size: 0.8125rem;
  color: var(--color-primary-dark);
  font-family: "Monaco", "Courier New", monospace;
  font-weight: 600;
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
  line-height: 1.4;
  text-align: left;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 2px 6px rgba(44, 95, 45, 0.1);
  }

  @media (max-width: 900px) {
    font-size: 0.72rem;
    padding: 0.45rem 0.55rem;
  }

  @media (max-width: 480px) {
    font-size: 0.68rem;
    padding: 0.4rem 0.5rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  justify-content: flex-end;
  width: auto;
  position: relative;
  z-index: 1;
  flex-wrap: nowrap;

  @media (max-width: 640px) {
    gap: 0.25rem;

    .ant-tooltip {
      display: none !important;
      pointer-events: none !important;
    }

    button {
      opacity: 1 !important;
      visibility: visible !important;
      display: flex !important;
    }
  }
`;

const IconButton = styled(Button)`
  display: flex !important;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0;
  position: relative;
  z-index: 2;

  .anticon {
    font-size: 16px;
  }

  /* Ensure buttons remain visible on mobile after click - aggressive fixes */
  &,
  &:active,
  &:focus,
  &:focus-visible,
  &:visited,
  &:hover {
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
  }

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
    }

    &,
    &:active,
    &:focus,
    &:focus-visible {
      background: var(--color-primary) !important;
      border-color: var(--color-primary) !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: flex !important;
    }
  }

  &.ant-btn-dangerous {
    &:hover {
      background: var(--color-error);
      border-color: var(--color-error);
    }

    &,
    &:active,
    &:focus,
    &:focus-visible {
      background: var(--color-error) !important;
      border-color: var(--color-error) !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: flex !important;
    }
  }

  /* Default button states - ensure always visible */
  &,
  &:active,
  &:focus,
  &:focus-visible,
  &:visited {
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
  }

  @media (max-width: 640px) {
    /* Prevent any hiding on mobile */
    &,
    &:active,
    &:focus,
    &:focus-visible,
    &:visited,
    &:hover {
      opacity: 1 !important;
      visibility: visible !important;
      display: flex !important;
      transform: scale(0.95) !important;
      position: relative !important;
      z-index: 10 !important;
    }

    /* Prevent touch-action from hiding buttons */
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }
`;

const InfoBanner = styled.div`
  padding: 1.5rem;
  background: linear-gradient(
    135deg,
    var(--color-primary-glow) 0%,
    rgba(134, 228, 157, 0.1) 100%
  );
  border: 2px solid var(--color-primary);
  border-radius: 12px;
  margin-top: 2rem;

  @media ${device.mobile} {
    padding: 0.875rem 0.75rem !important;
    margin-top: 1.5rem;
    border-radius: 8px;
  }

  @media (max-width: 640px) {
    padding: 0.875rem 0.75rem !important;
  }
`;

const InfoContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  margin: 0;
  padding: 0;

  @media ${device.mobile} {
    gap: 0;
    width: 100%;
    margin: 0;
    padding: 0;
  }
`;

const InfoIcon = styled.div`
  line-height: 1;
  flex-shrink: 0;

  .anticon {
    font-size: 1.75rem;
    color: var(--color-primary-dark);
  }
`;

const InfoText = styled.div`
  font-size: 0.9375rem;
  color: var(--color-primary-darker);
  line-height: 1.5;
  text-align: center;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-spacing: 0.05em;
  margin: 0;
  padding: 0;
  width: 100%;

  strong {
    font-weight: 600;
    color: var(--color-primary-dark);
    margin-right: 0.25em;
  }

  @media ${device.mobile} {
    font-size: 0.9375rem;
    line-height: 1.4;
    margin: 0;
    padding: 0;
    width: 100%;
    word-spacing: normal;
  }
`;

const EmptyStateContainer = styled.div`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 24px;
  border: 2px solid #e2e8f0;
  margin: 2rem 0;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08);
  text-align: center;
  position: relative;
  overflow: hidden;

  @media ${device.mobile} {
    padding: 2.5rem 1rem;
    margin: 1rem 0;
    border-radius: 16px;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(44, 95, 45, 0.02) 0%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const EmptyStateHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;

  @media ${device.mobile} {
    margin-bottom: 2rem;
  }
`;

const EmptyStateIcon = styled.div`
  color: var(--color-primary);
  margin-bottom: 2rem;
  opacity: 0.8;
  position: relative;
  z-index: 1;

  .anticon {
    font-size: 2.98rem;
    filter: drop-shadow(0 4px 8px rgba(44, 95, 45, 0.15));
  }

  @media ${device.mobile} {
    margin-bottom: 1.5rem;

    .anticon {
      font-size: 2.34rem;
    }
  }
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 2.2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  text-align: center;
  position: relative;
  z-index: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  text-wrap: balance;

  /* Prevent orphaned words */
  &::after {
    content: "\u00A0"; /* Non-breaking space */
    display: inline-block;
    width: 0;
    overflow: hidden;
  }

  @media ${device.mobile} {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
    text-wrap: balance;
    max-width: 100%;
    padding: 0 0.5rem;
  }
`;

const EmptyStateSubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: #475569;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  z-index: 1;
  line-height: 1.65;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  word-spacing: 0.05em;
  text-wrap: balance;

  @media ${device.mobile} {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
    line-height: 1.55;
    text-wrap: balance;
    padding: 0 1rem;
    max-width: 100%;
  }
`;

const EmptyStateCTATop = styled.div`
  text-align: center;
  margin-top: 2rem;
  position: relative;
  z-index: 1;

  .ant-btn {
    height: 48px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    padding: 0 2rem;
    min-width: 240px;
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(44, 95, 45, 0.3);
    }

    &:active {
      transform: translateY(0);
    }

    .anticon {
      color: white !important;
    }
  }

  @media ${device.mobile} {
    .ant-btn {
      width: 100%;
      min-width: unset;
    }
  }
`;

const BenefitsSection = styled.div`
  margin: 3rem 0 2rem;
  text-align: center;
  position: relative;
  z-index: 1;

  @media ${device.mobile} {
    margin: 2rem 0 1.5rem;
  }
`;

const BenefitsSectionTitle = styled.h4`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  text-wrap: balance;

  @media ${device.mobile} {
    font-size: 1.25rem;
    text-wrap: balance;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BenefitCard = styled.div`
  padding: 2rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(44, 95, 45, 0.08);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    border-color: var(--color-primary-light);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15);
    transform: translateY(-4px);
    background: linear-gradient(135deg, white 0%, #f8fafc 100%);
  }

  @media ${device.mobile} {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const BenefitIcon = styled.div`
  margin-bottom: 1.25rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 2.5rem;
    color: var(--color-primary);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 2px 6px rgba(44, 95, 45, 0.15));
  }

  ${BenefitCard}:hover & .anticon {
    transform: scale(1.1);
    color: var(--color-primary-dark);
  }

  @media ${device.mobile} {
    margin-bottom: 1rem;

    .anticon {
      font-size: 2rem;
    }
  }
`;

const BenefitTitle = styled.h5`
  font-family: var(--font-primary);
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
  text-align: center;
  width: 100%;

  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const BenefitDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9375rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  text-align: center;
  width: 100%;

  @media ${device.mobile} {
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const CTASubtext = styled.div`
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.01em;
  position: relative;
  z-index: 1;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  text-wrap: balance;

  @media ${device.mobile} {
    font-size: 0.8125rem;
    text-wrap: balance;
  }
`;

export default MarketingListings;
